import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import {
	Checklist,
	PinAction,
	SliderCheckbox,
} from '@components/common';

import { useQueryAcquiredAssetOwners } from '@hooks/assets/useQueryAcquiredAssetOwners';
import { useQueryAssetOwnersForFilter } from '@hooks/organisation/useQueryAssetOwnersForFilter';
import { useOnClickOutside } from '@hooks/utils/useOnClickOutSide';

import { auth, map } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	applyFilterValues,
	resetRightSideControls,
	setIsOwnersOfRentedAssetsShown,
	setPinnedFilter,
} from '@slices/map';

import { sortObjectByKey } from '@utils/common';

import { IChecklistEntity } from '@interfaces/assets/asset';
import { IOrganisationEntity } from '@interfaces/organisation/organisations';

import { EControlsType, EOrganizationType, TEControlsType } from '@enums/common';

import controlStyles from '../../MapControls.module.scss';
import styles from './AssetOwnersFilter.module.scss';

interface IRentedAssetOwnersEntity {
	id: string;
	name: string;
}

const AssetOwnersFilter = () => {
	const { assetOwnersForFilter } = useQueryAssetOwnersForFilter();

	const { assetOwners: assetOwnersOfRentedAssets } = useQueryAcquiredAssetOwners();

	const {
		appliedAssetsFilterValues: { ownerId: assetOwnerIds },
		isOwnersOfRentedAssetsShown,
	} = useAppSelector(map);

	const {
		accessPermission: {
			networkOperatorOrganizationAdministrator,
			networkOperatorAssetViewer,
			networkOperatorAssetEnquirer,
			assetConsumerOrganizationAdministrator,
			assetConsumerAssetEnquirer,
			assetConsumerAssetViewer,
		},
	} = useAppSelector(auth);

	const dispatch = useAppDispatch();

	const containerClassname = cn(controlStyles.controlItemContainer, styles.container);

	const [assetOwners, setAssetOwners] = useState<Array<IOrganisationEntity | IRentedAssetOwnersEntity>>([]);
	const [fullData, setFullData] = useState<IChecklistEntity[]>([]);

	const [isPinned, setIsPinned] = useState(false);

	const prevAssetOwnerIds = useRef<string[]>(assetOwnerIds || []);
	const containerRef = useRef<HTMLDivElement>(null);

	const getSelectedAssetOwnerIds = useCallback(() => fullData.reduce<string[]>((acc, curr) => {
		if (curr.isChecked) acc.push(curr.value);

		return acc;
	}, []), [fullData]);

	const pinFilter = useCallback((filter: TEControlsType | null) => {
		return dispatch(setPinnedFilter(filter));
	}, [dispatch]);

	const haveAccessToAcquiredAssets = (
		networkOperatorOrganizationAdministrator ||
		networkOperatorAssetViewer ||
		networkOperatorAssetEnquirer ||
		assetConsumerOrganizationAdministrator ||
		assetConsumerAssetEnquirer ||
		assetConsumerAssetViewer
	);

	useEffect(() => {
		if (isOwnersOfRentedAssetsShown) {
			setAssetOwners(() => assetOwnersOfRentedAssets.map((owner) => ({
				...owner,
				name: owner.label,
			})));
		} else if (assetOwnersForFilter.length) {
			setAssetOwners(() => assetOwnersForFilter
				.filter((organisation) => organisation.types
					?.some((type) => type.name === EOrganizationType.assetOwner)));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [assetOwnersOfRentedAssets.length, isOwnersOfRentedAssetsShown, assetOwnersForFilter.length]);

	useEffect(() => {
		if (assetOwners.length) {
			const data = assetOwners.reduce<IChecklistEntity[]>((acc, curr) => {
				if ('isActive' in curr && curr.isActive && !isOwnersOfRentedAssetsShown) {
					acc.push({
						value: curr.id,
						label: curr.name,
						isChecked: !!assetOwnerIds?.includes(curr.id),
					});
				} else {
					acc.push({
						value: curr.id,
						label: curr.name,
						isChecked: !!assetOwnerIds?.includes(curr.id),
					});
				}

				return acc;
			}, []);

			setFullData(sortObjectByKey(data, 'label') as IChecklistEntity[]);
		}
	}, [assetOwnerIds, assetOwners, isOwnersOfRentedAssetsShown]);

	useEffect(() => {
		pinFilter(isPinned ? EControlsType.owners : null);
	}, [isPinned, pinFilter]);

	const handleChange = (data: IChecklistEntity[]) => setFullData(data);

	const handleReset = () => {
		setFullData(prevData => prevData.map(item => ({ ...item, isChecked: false })));

		dispatch(applyFilterValues({ assetOwnerIds: [] }));

		if (!isPinned) dispatch(resetRightSideControls());
	};

	const handleApply = () => {
		const selectedAssetOwnerIds = getSelectedAssetOwnerIds();

		dispatch(applyFilterValues({ assetOwnerIds: selectedAssetOwnerIds }));

		if (!isPinned) dispatch(resetRightSideControls());

		prevAssetOwnerIds.current = selectedAssetOwnerIds;
	};

	const handleChangeAssetOwnerForRenters = () => {
		dispatch(setIsOwnersOfRentedAssetsShown(!isOwnersOfRentedAssetsShown));
	};

	useOnClickOutside({
		refs: [containerRef],
		handler: () => dispatch(resetRightSideControls()),
		disableClickOutside: isPinned,
		fireEvent: 'click',
	});

	return (
		<div
			ref={containerRef}
			className={containerClassname}
		>
			<div className={styles.header}>
				<div className={styles.title}>Asset Owner:</div>
				<PinAction
					isPinned={isPinned}
					pinTooltip='Pin'
					setIsPinned={setIsPinned}
					tooltipPlacement='left'
					unpinTooltip='Unpin'
				/>
			</div>
			{haveAccessToAcquiredAssets && (
				<div className={styles.assetOwnerToggle}>
					<SliderCheckbox
						checked={isOwnersOfRentedAssetsShown}
						id='assetOwnersForRenter'
						isLabelAfter
						label='Show owners of acquired assets only'
						onChange={handleChangeAssetOwnerForRenters}
					/>
				</div>
			)}
			<hr />
			<Checklist
				classNames={{
					submissionContainer: styles.buttonsSubmissionContainer,
					itemsContainer: styles.checklistItemsContainer,
				}}
				clearLabel='Reset all'
				data={fullData}
				handleClear={handleReset}
				handleSubmit={handleApply}
				isSortedList
				onChange={handleChange}
				searchPlaceholder='Search by organisation name'
				submitLabel='Apply'
			/>
		</div>
	);
};

export default memo(AssetOwnersFilter);
